import { useEffect, useId, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DECIMALS, NumericKind, displayFormat, parseNumeric, round, toEditText } from './numericFormat';

/**
 * A number field that reads like German prose and hands back a number.
 *
 * Formatting happens on blur, not on every keystroke. A field that regroups while you type has to
 * put the caret back by hand after every insertion, and that is where masked inputs break — you
 * type in the middle of a number and land three characters away. So: while the field has focus it
 * shows plain digits and you edit them; when it loses focus the value is parsed, rounded and
 * written back grouped.
 *
 * What leaves this component is always a `number` or `null` — never locale text. Whoever stores or
 * sends the value never has to know that a German user typed a comma.
 */

interface Properties {
    value: number | null;
    onChange: (value: number | null) => void;
    kind: NumericKind;
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    ariaLabel?: string;
}

export default function NumericInput({
    value,
    onChange,
    kind,
    disabled,
    label,
    placeholder,
    ariaLabel,
}: Properties) {
    const fieldId = useId();
    const [text, setText] = useState<string>(() => displayFormat(kind).format(value ?? 0));
    const [focused, setFocused] = useState(false);

    // While the field has focus what is on screen is what the user is typing; overwriting it from
    // the outside would fight them mid-word.
    useEffect(() => {
        if (!focused) {
            setText(value === null || value === undefined ? '' : displayFormat(kind).format(value));
        }
    }, [value, kind, focused]);

    const commit = () => {
        setFocused(false);
        const parsed = parseNumeric(text);
        const rounded = parsed === null ? null : round(parsed, DECIMALS[kind]);

        setText(rounded === null ? '' : displayFormat(kind).format(rounded));
        if (rounded !== value) {
            onChange(rounded);
        }
    };

    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            {label ? (
                <Label htmlFor={fieldId} className={disabled ? 'opacity-50' : undefined}>
                    {label}
                </Label>
            ) : null}
            <Input
                id={fieldId}
                value={text}
                placeholder={placeholder}
                aria-label={ariaLabel}
                disabled={disabled}
                // Deliberately a text field, not a number input: a number input rejects the comma a
                // German keyboard produces and offers spinners nobody wants on a price.
                type="text"
                inputMode="decimal"
                className="text-right"
                onFocus={() => {
                    setFocused(true);
                    setText(toEditText(value, kind));
                }}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.currentTarget.blur();
                    }
                }}
                onChange={(event) => {
                    // Keep only what can be part of a number. A minus is dropped here too, so the
                    // field never shows a sign it would discard a moment later.
                    setText(event.target.value.replace(/[^\d.,]/g, ''));
                }}
            />
        </div>
    );
}
